import BarChartIcon from '@material-ui/icons/BarChart';
import WarningIcon from '@material-ui/icons/Warning';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import RoomIcon from '@material-ui/icons/Room';
import PlaylistAddCheckIcon from '@material-ui/icons/PlaylistAddCheck';
import ArchiveIcon from '@material-ui/icons/Archive';
import ReportProblemIcon from '@material-ui/icons/ReportProblem';
import PeopleIcon from '@material-ui/icons/People';
import CategoryIcon from '@material-ui/icons/Category';
import GavelIcon from '@material-ui/icons/Gavel';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import ScheduleIcon from '@material-ui/icons/Schedule';
import EventIcon from '@material-ui/icons/Event';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import AccountBalanceIcon from '@material-ui/icons/AccountBalance';

export const DASHBOARD_SUMMARY_DEFAULT = {
  total: 0,
  waiting: 0,
  inStorage: 0,
  checkedOut: 0,
  disposed: 0,
  overdueActions: 0,
  overdueReturn: 0,
  noCustody: 0,
  readyForDisposal: 0,
  avgDaysInEvidence: 0,
  byCategory: [],
  byRequiredAction: [],
  bySubmittingOfficer: [],
  aging: {
    buckets: [],
    totalActive: 0,
    avgDays: 0,
    criticalCount: 0,
    longTermCount: 0,
  },
  byStorageLocation: [],
  byTempLocation: [],
  recentSubmissions: [],
  approachingActionDates: [],
  overdueCheckouts: [],
  correctionStats: {
    total: 0,
    open: 0,
    resolved: 0,
  },
  correctionsByReason: [],
  correctionsByRoutedTo: [],
  overdueAudit: {
    days: 90,
    locations: [],
  },
};

export const DASHBOARD_DISPOSAL_RULE_MATCHES_PREVIEW_COUNT = 5;

export const DASHBOARD_CORRECTION_STAT_KEYS = {
  TOTAL: 'total',
  OPEN: 'open',
  RESOLVED: 'resolved',
};

export const DASHBOARD_CORRECTION_STAT_TILES = [
  { key: DASHBOARD_CORRECTION_STAT_KEYS.TOTAL, label: 'Total', navigateTo: 'recordsCorrectionAny' },
  { key: DASHBOARD_CORRECTION_STAT_KEYS.OPEN, label: 'Open', navigateTo: 'recordsCorrectionOpen' },
  {
    key: DASHBOARD_CORRECTION_STAT_KEYS.RESOLVED,
    label: 'Resolved',
    navigateTo: 'recordsCorrectionResolved',
  },
];

export const DASHBOARD_NAVIGATE_TARGETS = {
  CORRECTIONS_BY_REASON: 'recordsCorrectionByReason',
  CORRECTIONS_BY_ROUTED_TO: 'recordsCorrectionByRoutedTo',
};

export const CORRECTION_NAVIGATE_TARGETS = [
  DASHBOARD_NAVIGATE_TARGETS.CORRECTIONS_BY_REASON,
  DASHBOARD_NAVIGATE_TARGETS.CORRECTIONS_BY_ROUTED_TO,
];

export const AGING_REPORT_LAYOUT = {
  CHART_WIDTH: 610,
  LABEL_WIDTH: 170,
  MAX_FILL_LENGTH: 380,
  ROW_HEIGHT: 28,
};

export const CORRECTION_REQUESTS_SECTION_KEY = 'correctionRequests';

export const AGING_BUCKET_COLOR_CLASSES = [
  'dashboard-aging-report__fill--safe',
  'dashboard-aging-report__fill--caution',
  'dashboard-aging-report__fill--warning',
  'dashboard-aging-report__fill--elevated',
  'dashboard-aging-report__fill--critical',
];

export const DASHBOARD_KPI_CONFIG = [
  { key: 'total', label: 'Total Items', colorClass: 'primary', navigateTo: 'recordsAll' },
  {
    key: 'waiting',
    label: 'Awaiting Check-In',
    colorClass: 'purple',
    showPercentOfTotal: true,
    navigateTo: 'recordsStatusCategory:Waiting',
  },
  {
    key: 'inStorage',
    label: 'In Storage',
    colorClass: 'success',
    showPercentOfTotal: true,
    navigateTo: 'recordsStatusCategory:IN',
  },
  {
    key: 'checkedOut',
    label: 'Checked Out',
    colorClass: 'warning',
    showPercentOfTotal: true,
    navigateTo: 'recordsStatusCategory:OUT',
  },
  {
    key: 'overdueReturn',
    label: 'Overdue for Return',
    colorClass: 'danger',
    navigateTo: 'recordsOverdueReturn',
  },
  {
    key: 'disposed',
    label: 'Disposed',
    colorClass: 'neutral',
    showPercentOfTotal: true,
    navigateTo: 'recordsStatusCategory:Disposed',
  },
];

export const DASHBOARD_INVENTORY_HEALTH_CONFIG = [
  { key: 'avgDaysInEvidence', label: 'Avg Days', sub: 'in evidence' },
  {
    key: 'noCustody',
    label: 'No Custody',
    sub: 'items at risk',
    navigateTo: 'recordsNotDisposed',
  },
  {
    key: 'overdueActions',
    label: 'Overdue Actions',
    sub: 'past action date',
    navigateTo: 'recordsOverdueActionDate',
  },
  { key: 'readyForDisposal', label: 'Ready Disposal', sub: 'flagged', navigateTo: 'disposal' },
];

export const DASHBOARD_ACTION_REQUIRED_CONFIG = [
  { key: 'readyForDisposal', label: 'Ready for Disposal', navigateTo: 'disposal' },
  { key: 'overdueActions', label: 'Overdue Action Date', navigateTo: 'recordsOverdueActionDate' },
  { key: 'noCustody', label: 'No Custody Records', navigateTo: 'recordsNotDisposed' },
];

export const DASHBOARD_LAYOUT_ROWS = [
  [
    {
      key: 'inventoryHealth',
      title: 'Inventory Health',
      icon: BarChartIcon,
      accent: 'primary',
    },
    {
      key: 'actionRequired',
      title: 'Action Required',
      icon: WarningIcon,
      accent: 'danger',
    },
    {
      key: 'awaitingCheckIn',
      title: 'Awaiting Check-In',
      icon: HourglassEmptyIcon,
      accent: 'info',
    },
  ],
  [
    {
      key: 'inStorageByLocation',
      title: 'In Storage by Location',
      icon: RoomIcon,
      accent: 'primary',
    },
    { key: 'overdueAudit', title: 'Overdue Audit', icon: PlaylistAddCheckIcon, accent: 'danger' },
    {
      key: 'disposalRuleMatches',
      title: 'Disposal Rule Matches',
      icon: ArchiveIcon,
      accent: 'warning',
    },
  ],
  [
    {
      key: CORRECTION_REQUESTS_SECTION_KEY,
      title: 'Correction Requests',
      icon: ReportProblemIcon,
      accent: 'danger',
    },
    {
      key: 'correctionsByReason',
      title: 'Corrections by Reason',
      icon: ReportProblemIcon,
      accent: 'danger',
    },
    {
      key: 'correctionsByRoutedTo',
      title: 'Corrections by Routed To',
      icon: PeopleIcon,
      accent: 'primary',
    },
  ],
  [
    { key: 'itemsByCategory', title: 'Items by Category', icon: CategoryIcon, accent: 'primary' },
    { key: 'requiredActions', title: 'Required Actions', icon: GavelIcon, accent: 'purple' },
  ],
  [
    {
      key: 'topSubmittingOfficers',
      title: 'Top Submitting Officers',
      icon: PeopleIcon,
      accent: 'primary',
    },
    {
      key: 'recentSubmissions',
      title: 'Recent Submissions',
      icon: AccessTimeIcon,
      accent: 'primary',
    },
  ],
  [
    {
      key: 'evidenceAgingReport',
      title: 'Evidence Aging Report',
      icon: ScheduleIcon,
      accent: 'warning',
    },
    {
      key: 'approachingActionDates',
      title: 'Approaching Action Dates',
      icon: EventIcon,
      accent: 'warning',
    },
  ],
  [
    { key: 'overdueCheckouts', title: 'Overdue Checkouts', icon: ExitToAppIcon, accent: 'danger' },
    { key: 'agencySummary', title: 'Agency Summary', icon: AccountBalanceIcon, accent: 'primary' },
  ],
];

export const DASHBOARD_PLACEHOLDER_TEXT = 'No data available yet';
